import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { memo, useCallback, useMemo, useState } from 'react';
import { DisplayPageData } from 'src/components/display-page-data';
import Iconify from 'src/components/iconify';
import NoneFound from 'src/components/none-found';
import PageHeader from 'src/components/page-header';
import PageInfoCarousel from 'src/components/page-info-carousel';
import fleetsListImage from 'src/assets/images/route-buddy/fleets-list.png';
import fleetsDialogImage from 'src/assets/images/route-buddy/fleets-dialog.png';
import fleetsUnassignedImage from 'src/assets/images/route-buddy/fleets-unassigned.png';
import { useSettingsContext } from 'src/components/settings';
import { SortableHeader } from 'src/components/sortable-header';
import { TruxTableCell } from 'src/components/trux-table-cell';
import useModelData from 'src/hooks/use-model-data';
import { useSort } from 'src/hooks/use-sort';
import { EldVehicle } from 'src/models';
import axios, { endpoints } from 'src/utils/axios';
import { DispatcherFleetUser } from '../types';
import FleetEditDialog from './fleet-edit-dialog';

type FleetsTableProps = {
  fleetUsers: DispatcherFleetUser[];
  vehiclesById: Map<number, EldVehicle>;
  onSelectUser: (user: DispatcherFleetUser) => void;
};

function trucksSummary(user: DispatcherFleetUser, vehiclesById: Map<number, EldVehicle>) {
  const ids = user.fleet?.eldVehicleIds || [];
  if (ids.length === 0) {
    return '—';
  }
  const units = ids
    .map((id) => vehiclesById.get(id)?.vehicleUnitNumber)
    .filter(Boolean)
    .map((unit) => `#${unit}`);
  const shown = units.slice(0, 4).join(', ');
  return units.length > 4 ? `${shown} +${units.length - 4} more` : shown;
}

function notificationsSummary(user: DispatcherFleetUser) {
  const { fleet } = user;
  if (!fleet) {
    return '—';
  }
  const parts: string[] = [];
  if (fleet.notifyLate) parts.push(`Late >${fleet.lateThresholdHours}h`);
  if (fleet.notifyOffRoute) parts.push('Off-route');
  if (fleet.notifyTripStarted) parts.push('Started');
  if (fleet.notifyTripFinished) parts.push('Finished');
  if (fleet.notifyDisconnected) parts.push('Disconnected');
  if (fleet.notifyMissingDocs) parts.push('Missing docs');
  return parts.length > 0 ? parts.join(', ') : 'None';
}

const FleetsTable = memo(({ fleetUsers, vehiclesById, onSelectUser }: FleetsTableProps) => {
  const { sortedList, sortData } = useSort(fleetUsers, 'name');

  if (!sortedList) {
    return null;
  }

  return (
    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableHeader sortData={sortData} sortKey="name">
                Name
              </SortableHeader>
              <SortableHeader sortData={sortData} sortKey="email">
                Email
              </SortableHeader>
              <TruxTableCell align="left">Trucks</TruxTableCell>
              <TruxTableCell align="left">Email Notifications</TruxTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedList.map((fleetUser) => (
              <TableRow
                key={fleetUser.userId}
                hover
                onClick={() => onSelectUser(fleetUser)}
                sx={{ cursor: 'pointer' }}
              >
                <TruxTableCell align="left">{fleetUser.name}</TruxTableCell>
                <TruxTableCell align="left">{fleetUser.email}</TruxTableCell>
                <TruxTableCell align="left">{trucksSummary(fleetUser, vehiclesById)}</TruxTableCell>
                <TruxTableCell align="left">{notificationsSummary(fleetUser)}</TruxTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
});

type PageModel = {
  fleetUsers?: DispatcherFleetUser[];
  eldVehicles?: EldVehicle[];
};

const pageInfo = (
  <PageInfoCarousel
    steps={[
      {
        image: fleetsListImage,
        title: 'Make a dispatcher',
        text: 'First, go to the Manage Users page. Mark the user as a dispatcher there. Then they show up on this page.',
      },
      {
        image: fleetsDialogImage,
        title: 'Give them trucks',
        text: 'Click a dispatcher. Pick their trucks. They will see these trucks first on the Track page and on the map.',
      },
      {
        image: fleetsDialogImage,
        title: 'Choose the emails',
        text: 'Pick which emails the dispatcher gets. For example: the trip is late, the driver is off the route, the driver disconnected.',
      },
      {
        image: fleetsUnassignedImage,
        title: 'Check the red button',
        text: 'A red button on this page means some trucks belong to nobody. Nobody gets emails about those trucks. Click the button to see the list.',
      },
    ]}
  />
);

export default function Page() {
  const settings = useSettingsContext();

  const dataLoader = useCallback(
    () => ({
      fleetUsers: axios.get(endpoints.dispatcherFleets.findAll).then((response) => response.data),
      eldVehicles: axios.get(endpoints.eldVehicles.findAll).then((response) => response.data),
    }),
    []
  );

  const { data, updateData, loading, error } = useModelData<PageModel>(dataLoader);

  const [selectedUser, setSelectedUser] = useState<DispatcherFleetUser | null>(null);
  const [unassignedOpen, setUnassignedOpen] = useState(false);

  const activeVehicles = useMemo(
    () => (data?.eldVehicles || []).filter((vehicle) => vehicle.active),
    [data?.eldVehicles]
  );
  const vehiclesById = useMemo(
    () => new Map(activeVehicles.map((vehicle) => [vehicle.id, vehicle])),
    [activeVehicles]
  );

  const fleetUsers = useMemo(() => data?.fleetUsers || [], [data?.fleetUsers]);

  const unassignedUnits = useMemo(() => {
    const assigned = new Set<number>();
    fleetUsers.forEach((fleetUser) => {
      (fleetUser.fleet?.eldVehicleIds || []).forEach((id) => assigned.add(id));
    });
    return activeVehicles
      .filter((vehicle) => !assigned.has(vehicle.id))
      .map((vehicle) => `#${vehicle.vehicleUnitNumber}`);
  }, [fleetUsers, activeVehicles]);

  const doubleAssignedUnits = useMemo(() => {
    const lines: string[] = [];
    activeVehicles.forEach((vehicle) => {
      const owners = fleetUsers.filter((fleetUser) =>
        fleetUser.fleet?.eldVehicleIds?.includes(vehicle.id)
      );
      if (owners.length > 1) {
        lines.push(`#${vehicle.vehicleUnitNumber} (${owners.map((owner) => owner.name).join(', ')})`);
      }
    });
    return lines;
  }, [fleetUsers, activeVehicles]);

  const onUserSaved = (updated: DispatcherFleetUser) => {
    updateData('fleetUsers')(
      fleetUsers.map((fleetUser) => (fleetUser.userId === updated.userId ? updated : fleetUser))
    );
    setSelectedUser(null);
  };

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        <PageHeader title="Dispatcher Fleets" info={pageInfo}>
          {unassignedUnits.length > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<Iconify icon="solar:danger-triangle-bold" />}
              onClick={() => setUnassignedOpen(true)}
            >
              Unassigned Trucks ({unassignedUnits.length})
            </Button>
          )}
        </PageHeader>

        {fleetUsers.length === 0 && (
          <NoneFound
            title="No Dispatchers Found"
            message="Mark users as dispatchers on the Manage Users page and they will show up here for truck assignment."
          />
        )}

        {fleetUsers.length > 0 && (
          <Stack direction="column" gap={2}>
            {doubleAssignedUnits.length > 0 && (
              <Alert severity="warning">
                Trucks in more than one fleet: {doubleAssignedUnits.join('; ')}
              </Alert>
            )}

            <FleetsTable
              fleetUsers={fleetUsers}
              vehiclesById={vehiclesById}
              onSelectUser={setSelectedUser}
            />
          </Stack>
        )}

        <Drawer anchor="right" open={unassignedOpen} onClose={() => setUnassignedOpen(false)}>
          <Box sx={{ width: { xs: '100vw', sm: 360 }, p: 2 }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Unassigned Trucks ({unassignedUnits.length})
              </Typography>
              <IconButton onClick={() => setUnassignedOpen(false)}>
                <Iconify icon="eva:close-fill" />
              </IconButton>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              These trucks are in no dispatcher&apos;s fleet, so nobody gets trip alerts for them.
            </Typography>
            <List dense>
              {unassignedUnits.map((unit) => (
                <ListItem key={unit} disableGutters divider>
                  <ListItemText primary={unit} />
                </ListItem>
              ))}
            </List>
          </Box>
        </Drawer>

        {selectedUser && (
          <FleetEditDialog
            user={selectedUser}
            eldVehicles={activeVehicles}
            allFleetUsers={fleetUsers}
            onSaved={onUserSaved}
            onCancel={() => setSelectedUser(null)}
          />
        )}
      </DisplayPageData>
    </Container>
  );
}
